import environment from '@/environment';
import {logger} from '@/initializers/logger';
import {stringToClickNameAction} from '@/services/analytics/string-to-click-name-action';
import {WEB_CLICK_TRACKING_KEY} from '@/services/analytics/web-click-tracking-key';
import {
  WebClickEvent,
  WebUploadEvent,
  WebSearchEvent,
  WebPageViewEvent,
  WebFormStartedEvent,
  WebFormSubmittedEvent,
  WebViewContentEvent,
  UserRolePropertyEvent,
  AnalyticsEvent,
  TrackingBasePrefix,
  TrackingClickPrefix,
  TrackingModifierSuffix,
} from './analytics-types';

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    dataLayer: any[];
  }
}

const SYSTEM_NAME = `${TrackingBasePrefix.AISF}:bmw`;

const getFormIdKey = (formId: string | null | undefined) => `${TrackingBasePrefix.BMW}:${formId || 'unknown'}`;

const sendEvent = (event: AnalyticsEvent) => {
  if (!environment.GOOGLE_TAG_MANAGER_CONTAINER_ID || typeof window === 'undefined' || !window.dataLayer) {
    logger.debug(JSON.stringify(event));
    return;
  }

  window.dataLayer.push(event);
  window.dataLayer.push({event_detail: {}}); // Ensure event_detail is reset after each event, required by analytics plan
};

type ClickNameBuildParams = {
  click_as: TrackingClickPrefix;
  click_action: (typeof WEB_CLICK_TRACKING_KEY)[keyof typeof WEB_CLICK_TRACKING_KEY];
  click_modifier_suffix?: TrackingModifierSuffix | null;
};

const buildWebClickClickName = (clickNameBuildParams: ClickNameBuildParams) => {
  const {click_as, click_action, click_modifier_suffix} = clickNameBuildParams;
  const base = `${click_as}:${click_action}` as const;

  if (click_modifier_suffix) {
    return `${base}${click_modifier_suffix}` as const;
  }

  return base;
};

const buildWebPageViewPageName = (pageName: string) => {
  return `${TrackingBasePrefix.AISF}:${TrackingBasePrefix.BMW}:${pageName}`;
};

export const analyticsHandler = {
  webClick: (data: Omit<WebClickEvent['event_detail'], 'click_name' | 'click_id'> & ClickNameBuildParams) => {
    const event: WebClickEvent = {
      event: 'web_click',
      event_detail: {
        click_name: buildWebClickClickName(data),
        click_id: null,
        click_text: data.click_text,
        click_url: data.click_url,
      },
    };
    sendEvent(event);
  },

  webUpload: (event_detail: WebUploadEvent['event_detail']) => {
    const event: WebUploadEvent = {event: 'web_upload', event_detail};
    sendEvent(event);
  },

  webSearch: (event_detail: WebSearchEvent['event_detail']) => {
    const event: WebSearchEvent = {event: 'web_search', event_detail};
    sendEvent(event);
  },

  webPageView: ({page_name, ...context}: Omit<WebPageViewEvent['context'], 'system_name'>) => {
    const event: WebPageViewEvent = {
      event: 'web_pv',
      context: {
        page_name: buildWebPageViewPageName(page_name),
        system_name: SYSTEM_NAME,
        ...context,
      },
      event_detail: {},
    };
    sendEvent(event);
  },

  webFormStarted: ({form_id}: Omit<WebFormStartedEvent['event_detail'], 'form_name'>) => {
    const event: WebFormStartedEvent = {
      event: 'web_form_started',
      event_detail: {
        form_id: getFormIdKey(form_id),
        form_name: 'nouvelle-demande',
      },
    };
    sendEvent(event);
  },

  webFormSubmitted: ({form_id}: Omit<WebFormSubmittedEvent['event_detail'], 'form_name'>) => {
    const event: WebFormSubmittedEvent = {
      event: 'web_form_submitted',
      event_detail: {
        form_id: getFormIdKey(form_id),
        form_name: 'nouvelle-demande',
      },
    };
    sendEvent(event);
  },

  webViewContent: ({content_name, ...event_detail}: WebViewContentEvent['event_detail']) => {
    const event: WebViewContentEvent = {
      event: 'web_view_content',
      event_detail: {
        content_name: stringToClickNameAction(content_name),
        ...event_detail,
      },
    };
    sendEvent(event);
  },

  userRoleProperty: (event_detail: UserRolePropertyEvent['event_detail']) => {
    const event: UserRolePropertyEvent = {event: 'user_role_property', event_detail};
    sendEvent(event);
  },
};
